import { HttpErrorResponse } from '@angular/common/http';
import { createReducer, on } from '@ngrx/store';
import { DatasetActions } from '@mode/dataset/contract';
import { Dataset } from '@mode/dataset/contract';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { ReportActions } from '@mode/reports/domain';
import { ExportedDatasetSourceContent } from '@mode/shared/contract-common';

export const datasetFeatureKey = 'dataset';

export interface DatasetState {
  dataset: Dataset | null;
  deleted: boolean;
  loadDatasetError: HttpErrorResponse | null;
  deleteFromCollectionToken: string | null;
  exportedDatasetSource: ExportedDatasetSourceContent;
}

export const datasetInitialState: DatasetState = {
  dataset: null,
  deleted: false,
  loadDatasetError: null,
  deleteFromCollectionToken: null,
  exportedDatasetSource: {
    cellContent: '',
    executionCount: 0,
  },
};

export const datasetReducer = createReducer(
  datasetInitialState.dataset,
  on(DatasetActions.deleteDataset, (state) => state),
  on(DatasetActions.deleteDatasetSuccess, (state, action) => null),
  on(DatasetActions.deleteDatasetFail, (state, action) => state),
  on(DatasetActions.loadDatasetSuccess, (state, action) => ({
    token: action.token,
    result: action.datasetResult,
    switchView: action.switchView,
    details: action.details,
  })),

  on(DatasetActions.loadDatasetReportEditRouteSuccess, (state, { dataset }) => dataset),

  on(DatasetActions.googleSheetSyncCompleted, (state, action) => ({
    token: action.dataView.datasetResult.datasetToken,
    result: action.dataView.datasetResult,
    switchView: action.dataView.switchView,
    details: action.dataView.details,
  })),

  // This action is listening for when the modern ngrx report rename method fires.
  on(
    ReportActions.apiRenameReportSuccess,
    (state, action) =>
      ({
        ...state,
        details: {
          ...state?.details,
          name: action.report.name,
        },
      }) as Dataset
  ),

  on(
    DatasetActions.publishDatasetSuccess,
    (state, action) =>
      ({
        ...state,
        details: {
          ...state?.details,
          name: action.name,
          thoughtspot: action.datasetPublicationResponse,
        },
      }) as Dataset
  ),

  on(DatasetActions.republishDatasetSuccess, (state) => state),

  on(
    DatasetActions.unpublishDatasetSuccess,
    (state) =>
      ({
        ...state,
        details: {
          ...state?.details,
          thoughtspot: undefined,
        },
      }) as Dataset
  ),

  // This action is listening from legacy land.
  // Moreover, for rename changes within the header dropdown
  on(ReportActions.updateHeaderReportDataManually, (state, action) => {
    if (!state) return null;

    return {
      ...state,
      details: {
        ...state?.details,
        name: action.headerReportData?.name,
      },
    } as Dataset;
  })
);

export const deletedReducer = createReducer(
  datasetInitialState.deleted,
  on(DatasetActions.deleteDataset, (state) => state),
  on(DatasetActions.deleteDatasetSuccess, (state, action) => true),
  on(DatasetActions.deleteFromCollectionView, (state, action) => true),
  on(DatasetActions.deleteDatasetFail, (state, action) => false)
);

export const loadDatasetErrorReducer = createReducer(
  datasetInitialState.loadDatasetError,
  on(DatasetActions.loadDatasetViewRoute, () => null),
  on(DatasetActions.loadDatasetEditRoute, () => null),
  on(DatasetActions.loadDatasetSuccess, () => null),
  on(DatasetActions.loadDatasetFailure, (_, action) => action.error)
);

export const deletedFromCollectionReducer = createReducer(
  datasetInitialState.deleteFromCollectionToken,
  on(DatasetActions.deleteFromCollectionView, (_, action) => action.token)
);

export const exportedDatasetSourceReducer = createReducer(
  datasetInitialState.exportedDatasetSource,
  on(DatasetActions.fetchExportedDatasetSource, (state) => state),
  on(DatasetActions.fetchExportedDatasetSourceSuccess, (_, action) => action.response),
  on(DatasetActions.fetchExportedDatasetSourceFailure, (state) => state)
);

export const reducer = {
  dataset: datasetReducer,
  deleted: deletedReducer,
  loadDatasetError: loadDatasetErrorReducer,
  deleteFromCollectionToken: deletedFromCollectionReducer,
  exportedDatasetSource: exportedDatasetSourceReducer,
};
